import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { money } from "@/lib/cobranza";
import { AlertTriangle, RotateCcw, Trash2, ShieldCheck } from "lucide-react";

// Gestión de recargos (moratorios) exclusiva de Dirección/Administración.
// Permite seleccionar y ELIMINAR recargos de un alumno con motivo + confirmación.
// El saldo se recalcula al vuelo y se refleja en admin y Portal de Padres.
export const RecargosManager = ({ studentId, canManage, onChanged }) => {
  const [data, setData] = useState(null);
  const [sel, setSel] = useState({});
  const [reason, setReason] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/direccion/student/${studentId}/recargos`);
      setData(r.data); setSel({});
    } catch (e) {
      if (e.response?.status === 403) setData({ forbidden: true });
    }
  };
  useEffect(() => { if (studentId && canManage) load(); /* eslint-disable-next-line */ }, [studentId, canManage]);

  if (!canManage || !data || data.forbidden) return null;

  const activos = (data.recargos || []).filter((r) => r.late_fee > 0 && !r.waived);
  const condonados = (data.recargos || []).filter((r) => r.waived);
  const selected = Object.keys(sel).filter((k) => sel[k]);

  const doWaive = async () => {
    if (!reason.trim()) { toast.error("Indica el motivo de la eliminación."); return; }
    setBusy(true);
    try {
      const r = await api.post(`/direccion/student/${studentId}/recargos/waive`, { periods: selected, reason: reason.trim() });
      setData(r.data); setSel({}); setReason(""); setConfirming(false);
      toast.success("Recargo(s) eliminado(s). Saldo actualizado.");
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo eliminar el recargo");
    } finally { setBusy(false); }
  };

  const doRestore = async (period) => {
    setBusy(true);
    try {
      const r = await api.post(`/direccion/student/${studentId}/recargos/restore`, { periods: [period] });
      setData(r.data);
      toast.success("Recargo restaurado.");
      onChanged && onChanged();
    } catch { toast.error("No se pudo restaurar el recargo"); }
    finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-4" data-testid="recargos-manager">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display font-semibold text-lg text-navy flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-amber-600" /> Recargos / moratorios
          <span className="text-[10px] font-semibold uppercase tracking-wider bg-amber-200 text-amber-800 rounded-full px-2 py-0.5">Dirección</span>
        </h3>
        <span className="text-xs text-nardo-500">Adeudo total: <strong className="text-navy font-mono">{money(data.total_due)}</strong></span>
      </div>

      {activos.length === 0 && condonados.length === 0 ? (
        <p className="text-sm text-nardo-500">Este alumno no tiene recargos aplicados.</p>
      ) : (
        <>
          {activos.length > 0 && (
            <div className="space-y-2 mb-3">
              <div className="text-[10px] uppercase tracking-[0.2em] text-amber-700">Recargos activos (selecciona para eliminar)</div>
              {activos.map((r) => (
                <label key={r.period} data-testid={`recargo-row-${r.period}`} className="flex items-center gap-3 bg-white border border-amber-200 rounded-md px-3 py-2 cursor-pointer">
                  <input type="checkbox" data-testid={`recargo-check-${r.period}`} checked={!!sel[r.period]}
                    onChange={(e) => setSel((s) => ({ ...s, [r.period]: e.target.checked }))}
                    className="w-4 h-4 accent-amber-600" />
                  <span className="flex-1 text-sm text-navy capitalize">{r.label}</span>
                  <span className="text-sm font-mono text-red-600">+{money(r.late_fee)}</span>
                </label>
              ))}
              <div className="pt-1">
                <textarea value={reason} onChange={(e) => setReason(e.target.value)} data-testid="recargo-reason"
                  placeholder="Motivo de la eliminación (obligatorio)…" rows={2}
                  className="w-full text-sm border border-amber-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-400" />
                <button data-testid="recargo-waive-btn" disabled={busy || selected.length === 0}
                  onClick={() => { if (!reason.trim()) { toast.error("Indica el motivo de la eliminación."); return; } setConfirming(true); }}
                  className="mt-2 inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-md px-4 py-2 disabled:opacity-50">
                  <Trash2 className="w-4 h-4" /> Eliminar recargo{selected.length > 1 ? "s" : ""} seleccionado{selected.length > 1 ? "s" : ""} ({selected.length})
                </button>
              </div>
            </div>
          )}

          {condonados.length > 0 && (
            <div className="space-y-2 border-t border-amber-200 pt-3">
              <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-700">Recargos eliminados</div>
              {condonados.map((r) => (
                <div key={r.period} data-testid={`recargo-waived-${r.period}`} className="flex items-center gap-3 bg-white border border-emerald-200 rounded-md px-3 py-2">
                  <span className="flex-1 text-sm text-navy capitalize">{r.label} <span className="text-emerald-700 text-xs">· eliminado</span></span>
                  <button data-testid={`recargo-restore-${r.period}`} disabled={busy} onClick={() => doRestore(r.period)}
                    className="inline-flex items-center gap-1 text-xs text-navy border border-navy/30 hover:bg-navy hover:text-white rounded-md px-2.5 py-1.5 disabled:opacity-50">
                    <RotateCcw className="w-3.5 h-3.5" /> Restaurar
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {confirming && (
        <div className="fixed inset-0 z-[1100] bg-black/50 flex items-center justify-center p-4" data-testid="recargo-confirm-overlay">
          <div className="bg-white rounded-xl max-w-md w-full p-6 shadow-2xl">
            <div className="flex items-center gap-3 mb-3 text-amber-600">
              <AlertTriangle className="w-6 h-6" />
              <h4 className="font-display font-semibold text-lg text-navy">Confirmar eliminación de recargo</h4>
            </div>
            <p className="text-sm text-nardo-600 mb-2">Vas a eliminar <strong>{selected.length}</strong> recargo(s). El saldo del alumno se recalculará de inmediato en administración y en el Portal de Padres.</p>
            <p className="text-sm text-nardo-600 mb-4">Motivo: <em className="text-navy">“{reason.trim()}”</em></p>
            <div className="flex justify-end gap-2">
              <button data-testid="recargo-confirm-cancel" disabled={busy} onClick={() => setConfirming(false)} className="px-4 py-2 rounded-md text-sm border border-nardo-200 text-nardo-600 hover:bg-nardo-50">Cancelar</button>
              <button data-testid="recargo-confirm-ok" disabled={busy} onClick={doWaive} className="px-4 py-2 rounded-md text-sm bg-red-600 hover:bg-red-700 text-white font-medium disabled:opacity-50">{busy ? "Eliminando…" : "Sí, eliminar"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
